import {
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DateField } from "./DateField";

type DateEndpoint = {
  name: string;
  label?: string;
};

export type DateRangeFieldProps<T extends FieldValues> = {
  label: string;
  from: DateEndpoint;
  to: DateEndpoint;
  stacked?: boolean;
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  copyFromToWhenToEmpty?: boolean;
  required?: boolean;
  className?: string;
};

export function DateRangeField<T extends FieldValues>({
  label,
  from,
  to,
  stacked = false,
  register,
  setValue,
  watch,
  copyFromToWhenToEmpty = false,
}: DateRangeFieldProps<T>) {
  const { t } = useTranslation();
  const translatedLabel = t(label, { defaultValue: label });
  const fromName = from.name as Path<T>;
  const toName = to.name as Path<T>;
  const fromValue = watch(fromName);
  const toValue = watch(toName);

  useEffect(() => {
    if (!copyFromToWhenToEmpty) return;
    if (fromValue == null || fromValue === "") {
      if (toValue != null && toValue !== "") {
        // Code enters here <=> toValue is not null but fromValue is null
        setValue(fromName, toValue as PathValue<T, Path<T>>, {
          shouldDirty: true,
        });
      }
      return;
    }
    if (toValue != null && toValue !== "") return;
    // Code enters here <=> fromValue is not null but toValue is null
    setValue(toName, fromValue as PathValue<T, Path<T>>, { shouldDirty: true });
  }, [
    // copyFromToWhenToEmpty, setValue, toName
    // No need to include these parameters in the dependency array since
    // they are not subject to change while the component is rendered
    fromValue,
    toValue,
  ]);

  return (
    <fieldset className="space-y-2">
      <legend className="text-xs text-blue-600">{translatedLabel}</legend>

      <div
        className={
          stacked ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"
        }
      >
        <DateField
          name={from.name}
          label={
            from.label
              ? t(from.label, { defaultValue: from.label })
              : t("dateRange.from")
          }
          labelPlacement="outside-left"
          register={register}
          setValue={setValue}
          watch={watch}
        />

        <DateField
          name={to.name}
          label={
            to.label
              ? t(to.label, { defaultValue: to.label })
              : t("dateRange.to")
          }
          labelPlacement="outside-left"
          register={register}
          setValue={setValue}
          watch={watch}
        />
      </div>
    </fieldset>
  );
}
